'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { EMPTY_OBJECT } from '#pplx/frontend/common/defaults.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { MAX_CHARACTERS_ALLOWED_TO_PASTE } from '#pplx/frontend/composer-sdk/internal/input/commands/pasteCommand.js';
import { useEffect, useRef } from 'react';
function getClipboardPaste(dataTransfer, oversizedText) {
    const files = Array.from(dataTransfer.files);
    if (files.length === 0) {
        files.push(...Array.from(dataTransfer.items)
            .filter((item) => item.kind === 'file')
            .map((item) => item.getAsFile())
            .filter((file) => file !== null));
    }
    switch (oversizedText) {
        case undefined:
        case 'discard':
            return [files];
        case 'file': {
            const text = dataTransfer.getData('text/plain');
            if (text.length > MAX_CHARACTERS_ALLOWED_TO_PASTE) {
                // Collect both sources before delivery so mixed pastes trigger consumer policy once.
                const oversizedTextFile = new File([text], 'paste.txt', { type: 'text/plain' });
                files.push(oversizedTextFile);
                return [files, { oversizedTextFile }];
            }
            return [files];
        }
        default:
            return assertUnreachableValue(oversizedText);
    }
}
/** Routes clipboard files to the single active file-paste capability. */
export function FilePasteCapabilityHost({ plugins, registerPasteHandler, disabled = false, }) {
    const pastePlugins = plugins.filter((plugin) => plugin.filePaste !== undefined);
    if (pastePlugins.length > 1) {
        throw new Error(`<Composer> may mount only one 'filePaste' capability; received ${pastePlugins.length}.`);
    }
    const plugin = pastePlugins[0];
    // Nothing to register until both an editor handler and a file-paste plugin are available.
    if (registerPasteHandler === null || plugin?.filePaste === undefined) {
        return null;
    }
    const config = plugin.filePaste(plugin.props ?? EMPTY_OBJECT);
    return (_jsx(ActiveFilePasteCapabilityHost, { config: config, disabled: disabled, registerPasteHandler: registerPasteHandler }));
}
function ActiveFilePasteCapabilityHost({ config, disabled, registerPasteHandler, }) {
    const configRef = useRef(config);
    configRef.current = config;
    const disabledRef = useRef(disabled);
    disabledRef.current = disabled;
    useEffect(() => {
        return registerPasteHandler((event) => {
            const currentConfig = configRef.current;
            if (disabledRef.current || currentConfig.disabled === true) {
                return;
            }
            const clipboardData = event.clipboardData;
            if (clipboardData === null) {
                return;
            }
            const paste = getClipboardPaste(clipboardData, currentConfig.oversizedText);
            if (paste[0].length === 0) {
                return;
            }
            // Match AskInput's ClipboardEvent-only path: preventing the paste suppresses the native
            // beforeinput event. Add InputEvent.dataTransfer handling only with a browser repro.
            event.preventDefault();
            currentConfig.onPaste(...paste);
        });
    }, [registerPasteHandler]);
    return null;
}
//# sourceMappingURL=FilePasteCapabilityHost.js.map